/**
 * Display-side money formatting. The wire format is a canonical decimal
 * **string** of integer smallest-units (e.g. `"12500"` = 125.00 GG with 2
 * decimal places). All math at rest is on bigints — this module only ever
 * converts to a presentation string at the edge.
 *
 * `GG_DECIMALS` is the canonical scale; if the platform ever bumps it,
 * change this single constant.
 */
export const GG_DECIMALS = 2;

/** Pad an integer smallest-unit string to `decimals` places. */
function padFractional(rawInteger: string, decimals: number): string {
  if (decimals === 0) return rawInteger;
  if (rawInteger.length <= decimals) {
    return `0.${rawInteger.padStart(decimals, "0")}`;
  }
  const whole = rawInteger.slice(0, rawInteger.length - decimals);
  const frac = rawInteger.slice(rawInteger.length - decimals);
  return `${whole}.${frac}`;
}

/**
 * Render a wire-format money string (`"12500"`) or bigint as a human
 * decimal string (`"125.00"`). Handles a leading `-` so signed balances
 * (e.g. `BalanceResponse.balance`) render correctly.
 */
export function formatMoney(amount: string | bigint, decimals = GG_DECIMALS): string {
  const raw = typeof amount === "bigint" ? amount.toString() : amount;
  if (!/^-?\d+$/.test(raw)) return raw; // pass through anything malformed unchanged
  const negative = raw.startsWith("-");
  const digits = negative ? raw.slice(1) : raw;
  const body = padFractional(digits, decimals);
  return negative ? `-${body}` : body;
}

/**
 * Sanitise a keystroke in a money field down to a valid in-progress decimal
 * amount: digits plus at most one decimal point with at most `decimals`
 * fractional digits. Every non-numeric character is dropped, so the field can
 * never hold letters — typing them is simply ignored rather than surfacing a
 * submit-time error. Runs on every `onInput`; {@link parseAmountInput} still
 * runs on submit to convert the clean value to wire format.
 */
export function sanitizeAmountInput(raw: string, decimals = GG_DECIMALS): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  // No fractional part allowed at this scale — keep the whole-number side only.
  if (decimals === 0) return cleaned.slice(0, dot);
  // Coerce a leading-dot amount ("." / ".5") to a "0" whole part, so a common
  // way of typing sub-1 amounts yields "0."/"0.5" — values `parseAmountInput`
  // accepts — instead of ".5", which its `^\d+` regex rejects on submit.
  const whole = cleaned.slice(0, dot) || "0";
  // Collapse any further dots and cap the fractional part at `decimals` digits.
  const frac = cleaned
    .slice(dot + 1)
    .replace(/\./g, "")
    .slice(0, decimals);
  return `${whole}.${frac}`;
}

/**
 * Parse a user-typed amount (`"125"`, `"125.5"`, `"0.01"`) into the wire
 * format the api expects — the canonical integer smallest-unit decimal
 * string. Returns `null` if the input can't be unambiguously parsed; the
 * caller renders an inline form error.
 */
export function parseAmountInput(input: string, decimals = GG_DECIMALS): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // Reject negatives — the api rejects them at the wire boundary anyway,
  // but failing here gives the user a clearer message.
  if (!/^\d+(?:\.\d+)?$/.test(trimmed)) return null;
  const [whole = "0", frac = ""] = trimmed.split(".");
  if (frac.length > decimals) return null;
  const padded = (frac + "0".repeat(decimals)).slice(0, decimals);
  // `BigInt` handles arbitrarily long strings; we only use it to strip
  // leading zeros consistently (`BigInt("0001").toString() === "1"`).
  const combined = BigInt(`${whole}${padded}`).toString();
  return combined;
}
